import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { FiEdit, FiTrash2, FiPlus, FiSearch, FiFilter, FiX, FiUser, FiMail, FiBriefcase, FiMapPin } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import AdminLayout from '../../../components/layout/AdminLayout';
import PageWrapper from '../../../components/layout/PageWrapper';
import Table from '../../../components/ui/Table';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import Select from '../../../components/ui/Select';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import Badge from '../../../components/ui/Badge';

import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { useFirestoreMutation } from '../../../hooks/useFirestoreMutation';
import {
  listarUsuarios,
  criarUsuario as apiCriarUsuario,
  atualizarUsuario as apiAtualizarUsuario,
  excluirUsuario as apiExcluirUsuario,
} from '../../../api/users.api';
import { IUsuario as Usuario, IUnidade as Unidade } from '../../../types/firestore.types';
import { ROLES } from '../../../constants/roles';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';

// Esquema de validação para o formulário de usuário
const userFormSchema = z.object({
  id: z.string().optional(),
  nome: z.string().min(1, 'Nome é obrigatório.'),
  email: z.string().email('Email inválido.').min(1, 'Email é obrigatório.'),
  senha: z.string().min(6, 'A senha deve ter no mínimo 6 caracteres.').optional().or(z.literal('')),
  tipo_usuario: z.enum([ROLES.ADMINISTRADOR, ROLES.OPERADOR], {
    message: 'Tipo de usuário inválido.',
  }),
  unidade_id: z.string().optional().nullable(),
});

type UserFormInputs = z.infer<typeof userFormSchema>;

// Tipos para os filtros
interface FiltrosUsuarios {
  searchTerm: string;
  tipoUsuario: string;
  unidadeId: string;
  status: string;
  dataInicio: string;
  dataFim: string;
}

const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [userToDeleteId, setUserToDeleteId] = useState<string | null>(null);
  
  // Estado para filtros
  const [filtros, setFiltros] = useState<FiltrosUsuarios>({
    searchTerm: '',
    tipoUsuario: '',
    unidadeId: '',
    status: '',
    dataInicio: '',
    dataFim: ''
  });

  // Mock de unidades - substitua pela sua fonte real de dados
  const unidades = [
    { id: '1', nome: 'Mr. Tugas Aeroporto' }, 
    { id: '2', nome: 'Mr. Tugas Cervejaria' }, 
    { id: '3', nome: 'Mr. Tugas Jardim Norte' }
  ];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<UserFormInputs>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      nome: '',
      email: '',
      senha: '',
      tipo_usuario: ROLES.OPERADOR,
      unidade_id: null,
    },
  });

  useEffect(() => {
    console.log('Erros do formulário:', errors);
  }, [errors]);
  
  const watchedUserType = watch('tipo_usuario');

  const {
    dados: usuarios,
    carregando: carregandoUsuarios,
    erro: erroUsuarios,
    refetch: refetchUsuarios,
  } = useFirestoreQuery<Usuario[]>('usuarios', [], listarUsuarios);

  const criarUsuarioMutation = useFirestoreMutation<Usuario, Omit<Usuario, 'id' | 'criado_em' | 'atualizado_em'>>(
    'usuarios',
    apiCriarUsuario,
    'criarUsuario'
  );
  const atualizarUsuarioMutation = useFirestoreMutation<Usuario, Partial<Usuario>>(
    'usuarios',
    apiAtualizarUsuario,
    'atualizarUsuario'
  );
  const excluirUsuarioMutation = useFirestoreMutation<string, string>(
    'usuarios',
    apiExcluirUsuario,
    'excluirUsuario'
  );

  useEffect(() => {
    if (editingUser) {
      setValue('id', editingUser.id);
      setValue('nome', editingUser.nome);
      setValue('email', editingUser.email);
      setValue('tipo_usuario', editingUser.tipo_usuario);
      setValue('unidade_id', editingUser.unidade_id || null);
      setValue('senha', ''); // Senha não é preenchida para edição
    } else {
      reset();
    }
  }, [editingUser, reset, setValue]);

  // Handlers para os filtros
  const handleFilterChange = (key: keyof FiltrosUsuarios, value: string) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      searchTerm: '',
      tipoUsuario: '',
      unidadeId: '',
      status: '',
      dataInicio: '',
      dataFim: ''
    });
  };

  const temFiltrosAtivos = useMemo(() => {
    return Object.values(filtros).some(value => value !== '');
  }, [filtros]);

  const openCreateModal = () => {
    setEditingUser(null);
    reset();
    setIsModalOpen(true);
  };

  const openEditModal = (usuario: Usuario) => {
    setEditingUser(usuario);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    reset();
  };

  const openConfirmDeleteModal = (id: string) => {
    setUserToDeleteId(id);
    setIsConfirmModalOpen(true);
  };

  const closeConfirmDeleteModal = () => {
    setIsConfirmModalOpen(false);
    setUserToDeleteId(null);
  };

  const handleDeleteConfirmed = async () => {
    if (userToDeleteId) {
      try {
        // Verificar se o usuário está tentando excluir a si mesmo
        if (currentUser && userToDeleteId === currentUser.uid) {
          toast.error('Você não pode excluir sua própria conta.');
          return;
        }

        await excluirUsuarioMutation.excluir(userToDeleteId);
        toast.success('Usuário excluído com sucesso!');
        refetchUsuarios();
      } catch (error) {
        toast.error('Erro ao excluir usuário.');
      } finally {
        closeConfirmDeleteModal();
      }
    }
  };

  const onSubmit = async (data: UserFormInputs) => {
    try {
      const now = Timestamp.now();

      console.log('Dados do formulário:', data);

      if (editingUser) {
        const updatedData: Partial<Usuario> = {
          nome: data.nome,
          email: data.email,
          tipo_usuario: data.tipo_usuario,
          unidade_id: data.unidade_id,
          atualizado_em: now,
        };

        // Senha NUNCA é gravada no Firestore: ela pertence ao Firebase Auth.
        // Para trocar a senha de alguém, use o fluxo de redefinição por e-mail
        // (resetarSenha no AuthContext) — o admin não define senha de terceiros.

        await atualizarUsuarioMutation.atualizar(editingUser.id, updatedData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        if (!data.senha) {
          toast.error('A senha é obrigatória para novos usuários.');
          return;
        }

        // Funcionário é criado pela Cloud Function: as regras do Firestore só
        // deixam o navegador criar 'cliente', e assim a sessão do admin não
        // é trocada pela do usuário novo.
        const resultado = await httpsCallable(getFunctions(), 'criarUsuarioInterno')({
          email: data.email,
          senha: data.senha,
          nome: data.nome,
          tipo_usuario: data.tipo_usuario,
          unidade_id: data.unidade_id || null,
        });
        const { success, error } = resultado.data as { success: boolean; error?: string };
        if (!success) {
          throw new Error(error || 'Falha ao criar usuário.');
        }
        toast.success('Usuário criado com sucesso!');
      }

      refetchUsuarios();
      closeModal();
    } catch (error) {
      toast.error(`Erro ao salvar usuário: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  // Filtragem avançada dos usuários
  const filteredUsers = useMemo(() => {
    if (!usuarios) return [];
    
    return usuarios.filter(user => {
      const matchesSearch = 
        user.nome.toLowerCase().includes(filtros.searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(filtros.searchTerm.toLowerCase());
      
      const matchesTipoUsuario = !filtros.tipoUsuario || user.tipo_usuario === filtros.tipoUsuario;
      
      const matchesUnidade = !filtros.unidadeId || user.unidade_id === filtros.unidadeId;
      
      const matchesStatus = !filtros.status || 
        (filtros.status === 'ativo' && user.ativo !== false) ||
        (filtros.status === 'inativo' && user.ativo === false);
      
      // Filtro por data de criação
      let matchesData = true;
      if (filtros.dataInicio && user.criado_em) {
        const dataCriacao = user.criado_em.toDate();
        const dataInicio = new Date(filtros.dataInicio);
        matchesData = matchesData && dataCriacao >= dataInicio;
      }
      
      if (filtros.dataFim && user.criado_em) {
        const dataCriacao = user.criado_em.toDate();
        const dataFim = new Date(filtros.dataFim);
        dataFim.setHours(23, 59, 59, 999); // Fim do dia
        matchesData = matchesData && dataCriacao <= dataFim;
      }

      return matchesSearch && matchesTipoUsuario && matchesUnidade && matchesStatus && matchesData;
    });
  }, [usuarios, filtros]);

  const unitOptions = useMemo(() => {
    return unidades.map(unidade => ({
      valor: unidade.id,
      label: unidade.nome
    }));
  }, [unidades]);

  // Estatísticas dos filtros
  const estatisticas = useMemo(() => {
    const total = usuarios?.length || 0;
    const filtrados = filteredUsers.length;
    const ativos = usuarios?.filter(u => u.ativo !== false).length || 0;
    const inativos = usuarios?.filter(u => u.ativo === false).length || 0;

    return { total, filtrados, ativos, inativos };
  }, [usuarios, filteredUsers]);

  const columns = useMemo(() => [
    {
      titulo: 'ID',
      chave: 'id',
      render: (item: Usuario) => (
        <span className="text-sm text-text-light font-mono">{item.id.substring(0, 8)}...</span>
      ),
    },
    {
      titulo: 'Nome',
      chave: 'nome',
      render: (item: Usuario) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
            <FiUser size={14} className="text-primary" />
          </div>
          <span className="font-medium text-text">{item.nome}</span>
        </div>
      ),
    },
    {
      titulo: 'Email',
      chave: 'email',
      render: (item: Usuario) => (
        <div className="flex items-center gap-2">
          <FiMail size={14} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">{item.email}</span>
        </div>
      ),
    },
    {
      titulo: 'Tipo de Usuário',
      chave: 'tipo_usuario',
      render: (item: Usuario) => (
        <div className="flex items-center gap-2">
          <FiBriefcase size={14} className="text-text-secondary" />
          <Badge
            variant={
              item.tipo_usuario === ROLES.ADMINISTRADOR ? 'primary' : 'default'
            }
          >
            {item.tipo_usuario.charAt(0).toUpperCase() + item.tipo_usuario.slice(1)}
          </Badge>
        </div>
      ),
    },
    {
      titulo: 'Unidade',
      chave: 'unidade_id',
      render: (item: Usuario) => {
        const unidade = unidades.find(u => u.id === item.unidade_id);
        return (
          <div className="flex items-center gap-2">
            <FiMapPin size={14} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">{unidade ? unidade.nome : 'N/A'}</span>
          </div>
        );
      },
    },
    {
      titulo: 'Status',
      chave: 'ativo',
      render: (item: Usuario) => (
        <Badge variant={item.ativo === false ? 'danger' : 'success'}>
          {item.ativo === false ? 'Inativo' : 'Ativo'}
        </Badge>
      ),
    },
    {
      titulo: 'Criado Em',
      chave: 'criado_em',
      render: (item: Usuario) => (
        <span className="text-sm text-text-secondary">
          {item.criado_em ? format(item.criado_em.toDate(), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : 'N/A'}
        </span>
      ),
    },
    {
      titulo: 'Ações',
      chave: 'acoes',
      render: (item: Usuario) => (
        <div className="flex space-x-2">
          <Button
            onClick={() => openEditModal(item)}
            tipo="secondary"
            className="p-2 rounded-full hover:bg-gradient-to-r from-[#c5a663] via-[#f2dd82] to-[#c5a663]/20 transition-colors duration-200"
            aria-label={`Editar usuário ${item.nome}`}
          >
            <FiEdit size={18} />
          </Button>
          <Button
            onClick={() => openConfirmDeleteModal(item.id)}
            tipo="danger"
            className="p-2 rounded-full hover:bg-danger/20 transition-colors duration-200"
            aria-label={`Excluir usuário ${item.nome}`}
            disabled={currentUser && item.id === currentUser.uid}
          >
            <FiTrash2 size={18} />
          </Button>
        </div>
      ),
    },
  ], [unidades, currentUser]);

  return (
    <AdminLayout>
      <PageWrapper>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6 p-6 bg-background rounded-lg shadow-xl"
        >
          {/* Cabeçalho */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-text-light mb-2">
                Gerenciamento de Usuários
              </h1>
              <p className="text-text-secondary">
                Gerencie os usuários do sistema e suas permissões
              </p>
            </div>
            <Button 
              onClick={openCreateModal} 
              tipo="primary" 
              className="flex items-center gap-2 px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
            >
              <FiPlus size={20} />
              Adicionar Usuário
            </Button>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-background-dark p-4 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-text-light">{estatisticas.total}</div>
              <div className="text-text-secondary text-sm">Total de Usuários</div>
            </div>
            <div className="bg-background-dark p-4 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-primary">{estatisticas.filtrados}</div>
              <div className="text-text-secondary text-sm">Filtrados</div>
            </div>
            <div className="bg-background-dark p-4 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-success">{estatisticas.ativos}</div>
              <div className="text-text-secondary text-sm">Usuários Ativos</div>
            </div>
            <div className="bg-background-dark p-4 rounded-lg border border-primary/20">
              <div className="text-2xl font-bold text-danger">{estatisticas.inativos}</div>
              <div className="text-text-secondary text-sm">Usuários Inativos</div>
            </div>
          </div>

          {/* Barra de Pesquisa e Filtros */}
          <div className="bg-background-dark p-4 rounded-lg border border-primary/20">
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="flex-1">
                <Input
                  tipo="text"
                  placeholder="Buscar por nome, email..."
                  value={filtros.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  className="bg-background border-primary/20 focus:border-primary text-text"
                  icon={<FiSearch className="text-text-secondary" />}
                />
              </div>
              <Button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                tipo="secondary"
                className="flex items-center gap-2"
              >
                <FiFilter size={18} />
                Filtros
                {temFiltrosAtivos && (
                  <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                    {Object.values(filtros).filter(v => v !== '').length}
                  </span>
                )}
              </Button>
              {temFiltrosAtivos && (
                <Button
                  onClick={limparFiltros}
                  tipo="danger"
                  className="flex items-center gap-2"
                >
                  <FiX size={18} />
                  Limpar
                </Button>
              )}
            </div>

            {/* Filtros Expandidos */}
            {isFiltersOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-primary/20"
              >
                <Select
                  label="Tipo de Usuário"
                  value={filtros.tipoUsuario}
                  onValueChange={(value) => handleFilterChange('tipoUsuario', value)}
                  opcoes={[
                    { valor: '', label: 'Todos os tipos' },
                    { valor: 'administrador', label: 'Administrador' },
                    { valor: 'operador', label: 'Operador' },
                    { valor: 'cliente', label: 'Cliente' },
                  ]}
                />
                
                <Select
                  label="Unidade"
                  value={filtros.unidadeId}
                  onValueChange={(value) => handleFilterChange('unidadeId', value)}
                  opcoes={[
                    { valor: '', label: 'Todas as unidades' },
                    ...unitOptions,
                  ]}
                />
                
                <Select
                  label="Status"
                  value={filtros.status}
                  onValueChange={(value) => handleFilterChange('status', value)}
                  opcoes={[
                    { valor: '', label: 'Todos os status' },
                    { valor: 'ativo', label: 'Ativo' },
                    { valor: 'inativo', label: 'Inativo' },
                  ]}
                />

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-text-secondary">
                    Período de Criação
                  </label>
                  <div className="flex gap-2">
                    <Input
                      tipo="date"
                      value={filtros.dataInicio}
                      onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
                      className="bg-background border-primary/20 focus:border-primary text-text"
                    />
                    <Input
                      tipo="date"
                      value={filtros.dataFim}
                      onChange={(e) => handleFilterChange('dataFim', e.target.value)}
                      className="bg-background border-primary/20 focus:border-primary text-text"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Tabela */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="overflow-x-auto rounded-lg border border-primary/20"
          >
            {carregandoUsuarios ? (
              <div className="flex justify-center items-center p-8">
                <Spinner size="lg" />
              </div>
            ) : erroUsuarios ? (
              <Alert tipo="error" mensagem="Erro ao carregar usuários. Tente novamente." />
            ) : (
              <Table
                colunas={columns}
                dados={filteredUsers}
                emptyMessage={
                  temFiltrosAtivos 
                    ? "Nenhum usuário encontrado com os filtros aplicados." 
                    : "Nenhum usuário cadastrado."
                }
                className="min-w-full bg-background-dark"
              />
            )}
          </motion.div>
        </motion.div>

        {/* Modal de Criar/Editar Usuário */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          titulo={editingUser ? 'Editar Usuário' : 'Adicionar Novo Usuário'}
          size="lg"
        >
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6 p-4"
          >
            <Input
              label="Nome Completo"
              {...register('nome')}
              placeholder="Nome do usuário"
              error={errors.nome?.message}
              className="bg-background-dark border-primary/20 focus:border-primary text-text"
            />

            <Input
              label="Email"
              {...register('email')}
              placeholder="email@exemplo.com"
              error={errors.email?.message}
              className="bg-background-dark border-primary/20 focus:border-primary text-text"
            />

            {!editingUser && (
              <Input
                label="Senha"
                type="password"
                {...register('senha')}
                placeholder="Senha do usuário"
                error={errors.senha?.message}
                className="bg-background-dark border-primary/20 focus:border-primary text-text"
              />
            )}

            <Controller
              name="tipo_usuario"
              control={control}
              render={({ field }) => (
                <Select
                  label="Tipo de Usuário"
                  value={field.value}
                  onValueChange={field.onChange}
                  opcoes={[
                    { valor: ROLES.OPERADOR, label: 'Operador' },
                    { valor: ROLES.SUPERVISOR, label: 'Supervisor' },
                    { valor: ROLES.GERENTE, label: 'Gerente' },
                    { valor: ROLES.ADMINISTRADOR, label: 'Administrador' },
                  ]}
                  error={errors.tipo_usuario?.message}
                  className="bg-background-dark border-primary/20 focus:border-primary text-text"
                />
              )}
            />

            {(watchedUserType === ROLES.OPERADOR || watchedUserType === ROLES.SUPERVISOR || watchedUserType === ROLES.GERENTE) && (
              <Controller
                name="unidade_id"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Unidade"
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    opcoes={[{ valor: '', label: 'Nenhuma unidade selecionada' }, ...unitOptions]}
                    error={errors.unidade_id?.message}
                    className="bg-background-dark border-primary/20 focus:border-primary text-text"
                  />
                )}
              />
            )}

            <div className="flex justify-end gap-4 mt-6">
              <Button
                type="button"
                onClick={closeModal}
                tipo="secondary"
                className="px-6 py-2 rounded-lg"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                tipo="primary"
                disabled={criarUsuarioMutation.carregando || atualizarUsuarioMutation.carregando}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
              >
                {(criarUsuarioMutation.carregando || atualizarUsuarioMutation.carregando) && <Spinner size="sm" color="text" />}
                {editingUser ? 'Salvar Alterações' : 'Adicionar Usuário'}
              </Button>
            </div>
          </motion.form>
        </Modal>

        {/* Modal de Confirmação de Exclusão */}
        <Modal
          isOpen={isConfirmModalOpen}
          onClose={closeConfirmDeleteModal}
          titulo="Confirmar Exclusão"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="p-4 text-text-secondary"
          >
            <p className="mb-6">Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.</p>
            
            {currentUser && userToDeleteId === currentUser.uid && (
              <Alert 
                tipo="error" 
                mensagem="Você não pode excluir sua própria conta." 
                className="mb-4"
              />
            )}
            
            <div className="flex justify-end gap-4">
              <Button
                onClick={closeConfirmDeleteModal}
                tipo="secondary"
                className="px-6 py-2 rounded-lg"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDeleteConfirmed}
                tipo="danger"
                disabled={excluirUsuarioMutation.carregando || (currentUser && userToDeleteId === currentUser.uid)}
                className="px-6 py-2 rounded-lg flex items-center gap-2"
              >
                {excluirUsuarioMutation.carregando && <Spinner size="sm" color="text" />}
                Excluir
              </Button>
            </div>
          </motion.div>
        </Modal>
      </PageWrapper>
    </AdminLayout>
  );
};

export default UserManagementPage;